/**
 * The platform's two marks, wherever a screen draws them (domain/branding.js).
 *
 * The built-in marks keep their old classes — the glow, and the inversion on
 * the light themes that turns their white lines to ink. An uploaded mark gets
 * neither: it is somebody's artwork, drawn whole at its own proportions on the
 * plate the Super Admin picked, so a wide wordmark is never squeezed into the
 * built-in mark's square and a logo in dark lines keeps its white plate on the
 * dark themes.
 *
 * Until the branding is known (a first visit, before GET /api/branding
 * answers) each mark is an empty space of its size, never the built-in mark
 * for a moment before the uploaded one.
 */

import { useBranding } from '../../services/branding';
import { logoPlateClass } from '../../domain/logoBackdrop';

/** Hide a logo that fails to load rather than leaving a broken-image box. */
const hideOnError = (event) => {
  event.currentTarget.style.display = 'none';
};

/** An uploaded mark: `size` caps its height and width, the plate wraps it. */
export function UploadedMark({ mark, size, className = '' }) {
  return (
    <img
      src={mark.src}
      alt={mark.alt}
      onError={hideOnError}
      className={`brand-upload ${logoPlateClass(mark.backdrop)} ${size} ${className}`}
      decoding="async"
    />
  );
}

/**
 * The developer's mark.
 *
 *   header   the top bar of every dashboard (from a tablet up)
 *   hero     above the sign-in card
 */
export function AgencyMark({ variant = 'header', className = '' }) {
  const brand = useBranding();
  const mark = brand.agency;
  const spec = AGENCY[variant] || AGENCY.header;
  if (!brand.ready) return <span aria-hidden="true" className={`${spec.space} ${className}`} />;
  if (mark.custom) return <UploadedMark mark={mark} size={spec.uploaded} className={className} />;
  return <img src={mark.src} alt={mark.alt} onError={hideOnError} className={`${spec.builtIn} ${className}`} />;
}

const AGENCY = {
  header: {
    space: 'inline-block h-12 w-24 shrink-0',
    builtIn: 'brand-mark logo-glow-sm h-12 shrink-0 object-contain',
    uploaded: 'brand-upload--header shrink-0',
  },
  hero: {
    space: 'block h-28 w-28 sm:h-32',
    builtIn: 'brand-mark logo-glow-lg h-28 object-contain opacity-95 sm:h-32',
    uploaded: 'brand-upload--hero',
  },
};

/**
 * The platform's mark.
 *
 *   sidebar  heads the dashboard's menu; the built-in one with the name and
 *            the strapline beside it, an uploaded one alone (it carries its
 *            own name) and wider
 *   card     inside the sign-in card, above the welcome
 *
 * The landing page draws its own built-in marks, larger and animated, and
 * uses UploadedMark for uploaded ones.
 */
export function ProductMark({ variant = 'sidebar', className = '' }) {
  const brand = useBranding();
  const mark = brand.product;
  if (!brand.ready) return <span aria-hidden="true" className={`${PRODUCT_SPACE[variant] || PRODUCT_SPACE.sidebar} ${className}`} />;

  if (variant === 'sidebar') {
    if (mark.custom) {
      return (
        <div className={`min-w-0 flex-1 ${className}`}>
          <UploadedMark mark={mark} size="brand-upload--sidebar" />
        </div>
      );
    }
    return (
      <div className={`flex min-w-0 flex-1 items-center gap-3 ${className}`}>
        <img
          src={mark.src}
          alt={mark.alt}
          onError={hideOnError}
          className="brand-mark h-12 w-12 shrink-0 rounded-xl bg-white/5 object-contain p-1"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold text-white">{brand.name}</p>
          <p className="truncate text-[11px] text-slate-400">بوابتك للرعاية الصحية</p>
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    if (mark.custom) return <UploadedMark mark={mark} size="brand-upload--card mb-4" className={className} />;
    return (
      <div className={`neon-glow mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/10 ${className}`}>
        <img src={mark.src} alt={mark.alt} onError={hideOnError} className="h-10 w-10 object-contain" />
      </div>
    );
  }

  return null;
}

const PRODUCT_SPACE = {
  sidebar: 'block h-14 min-w-0 flex-1',
  card: 'mb-4 block h-16 w-16',
};
